import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { lazy, memo, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Provider as DataSource, useReadData, useReset } from "react-flowder";
import { BrowserRouter, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import { GlobalSettingsProvider } from "~/shared/globals-settings";

import { accountQuery } from "./app/react/account";
import { anonymsUserQuery } from "./app/react/current-user";
import { globalSettingsQuery } from "./app/react/global-settings";
import PageLoading from "./components/page-loading";
import { NotFoundError, PermissionError, AuthorizationError } from "./shared/errors";
import { NotificationProvider } from "./shared/notification";

import type { FC, ReactNode } from "react";
import type { FallbackProps } from "react-error-boundary";

import "./app.css";
import "reset-css";
import "nprogress/nprogress.css";
import "dayjs/locale/ja";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

const NotFoundPage = lazy(() => import("./views/notfound"));
const UnknownErrorPage = lazy(() => import("./views/unknown-error"));
const Layout = lazy(() => import("./views/layout"));
const SignInPage = lazy(() => import("./views/signin"));
const FormPage = lazy(() => import("./views/form"));
const TransactionsPage = lazy(() => import("./views/transactions"));
const TransactionPage = lazy(() => import("./views/transaction"));

const SetupGlobalSettings: FC<{ children: ReactNode }> = ({ children }) => {
  const globalSettings = useReadData(globalSettingsQuery());

  return <GlobalSettingsProvider settings={globalSettings}>{children}</GlobalSettingsProvider>;
};

const App: FC = () => {
  return (
    <DataSource>
      <Suspense>
        <SetupGlobalSettings>
          <BrowserRouter>
            <NotificationProvider>
              <Routes>
                <Route path="/" element={<SharedLayout />}>
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/form" element={<FormPage />} />
                  <Route path="/transactions">
                    <Route index element={<TransactionsPage />} />
                    <Route path=":id" element={<TransactionRoute />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </NotificationProvider>
          </BrowserRouter>
        </SetupGlobalSettings>
      </Suspense>
    </DataSource>
  );
};

export default memo(App);

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const reset = useReset();
  const navigate = useNavigate();

  useEffect(() => {
    if (error instanceof PermissionError || error instanceof AuthorizationError) {
      navigate("/signin");
      reset();
      resetErrorBoundary();
    }
  }, [error, navigate, reset, resetErrorBoundary]);

  if (error instanceof NotFoundError) return <NotFoundPage />;

  return <UnknownErrorPage />;
};

const SharedLayout: FC = () => {
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoading />}>
      <Layout>
        <SentryUser />
        <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[location]}>
          <Outlet />
        </ErrorBoundary>
      </Layout>
    </Suspense>
  );
};

const TransactionRoute = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) throw new NotFoundError();

  return <TransactionPage id={id} />;
};

const SentryUser = () => {
  const user = useReadData(anonymsUserQuery());
  const account = useReadData(accountQuery());

  useEffect(() => {
    if (account !== null) {
      Sentry.setUser({
        id: account.id,
        uid: user?.id,
      });
    }
  }, [account, user?.id]);

  return null;
};
